/**
 * The on-chart legend row, with the controls TradingView puts there.
 *
 * Hiding is deliberately not the same as removing: a hidden indicator keeps
 * its settings and stays computed, so toggling it back is instant rather than
 * a refetch, and you do not lose a tuned length by glancing at the chart
 * without it.
 */
import type { IndicatorDef } from "@tt/shared";
import { useRef, useState } from "react";

import { IndicatorSettings } from "./IndicatorSettings";
import { Popover } from "./Popover";
import { ToolIcon } from "./ToolIcon";

export interface LegendSeries {
  key: string;
  name: string;
  color: string;
  value: string;
}

export interface LegendControls {
  visible: boolean;
  onToggleVisible: () => void;
  onRemove: () => void;
  /** Absent for a Pine run: there is one script, and a second copy of it would
   *  be the same source computing the same values. */
  onDuplicate?: () => void;
  onMovePane?: () => void;
  movePaneLabel?: string;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  /** Present for built-in indicators; a Pine script has no catalogue entry. */
  settings?: {
    def: IndicatorDef;
    params: Record<string, unknown>;
    onChange: (patch: Record<string, unknown>) => void;
  };
}

export function IndicatorLegend({
  title, series, controls, compact = false,
}: {
  title: string;
  series: LegendSeries[];
  controls?: LegendControls;
  compact?: boolean;
}) {
  const moreRef = useRef<HTMLButtonElement>(null);
  const gearRef = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const hidden = controls ? !controls.visible : false;

  return (
    <div className={`legend-row ${hidden ? "hidden" : ""}`}>
      <span className="legend-title">{title}</span>

      {!hidden &&
        series.map((s) => (
          <span className="legend-item" key={s.key}>
            <span className="legend-swatch" style={{ background: s.color }} />
            {!compact && series.length > 1 && <span>{s.name}</span>}
            <span style={{ color: "var(--text)" }}>{s.value}</span>
          </span>
        ))}
      {hidden && <span className="faint small">hidden</span>}

      {controls && (
        <span className="legend-controls">
          <button
            className="legend-btn"
            title={controls.visible ? "Hide" : "Show"}
            onClick={controls.onToggleVisible}
          >
            <ToolIcon name={controls.visible ? "eye" : "eye-off"} size={13} />
          </button>
          {controls.settings && (
            <button
              className="legend-btn"
              title="Settings"
              ref={gearRef}
              onClick={() => setSettingsOpen((o) => !o)}
            >
              <ToolIcon name="gear" size={13} />
            </button>
          )}
          <button
            className="legend-btn"
            title="More"
            ref={moreRef}
            onClick={() => setMenu((m) => !m)}
          >
            <ToolIcon name="more" size={13} />
          </button>
          <button className="legend-btn" title="Remove" onClick={controls.onRemove}>
            <ToolIcon name="close" size={13} />
          </button>

          <Popover anchorRef={moreRef} open={menu} onClose={() => setMenu(false)} width={190}>
            <div className="menu">
              {controls.settings && (
                <button
                  className="menu-item"
                  onClick={() => { setSettingsOpen(true); setMenu(false); }}
                >
                  Settings…
                </button>
              )}
              {controls.onDuplicate && (
                <button
                  className="menu-item"
                  onClick={() => { controls.onDuplicate?.(); setMenu(false); }}
                >
                  Duplicate
                </button>
              )}
              <button className="menu-item" onClick={() => { controls.onToggleVisible(); setMenu(false); }}>
                {controls.visible ? "Hide" : "Show"}
              </button>
              {controls.onMovePane && (
                <button className="menu-item" onClick={() => { controls.onMovePane!(); setMenu(false); }}>
                  {controls.movePaneLabel}
                </button>
              )}
              {(controls.onMoveUp || controls.onMoveDown) && <div className="menu-sep" />}
              {controls.onMoveUp && (
                <button className="menu-item" onClick={() => { controls.onMoveUp!(); setMenu(false); }}>
                  Move up
                </button>
              )}
              {controls.onMoveDown && (
                <button className="menu-item" onClick={() => { controls.onMoveDown!(); setMenu(false); }}>
                  Move down
                </button>
              )}
              <div className="menu-sep" />
              <button
                className="menu-item danger"
                onClick={() => { controls.onRemove(); setMenu(false); }}
              >
                Remove
              </button>
            </div>
          </Popover>

          {controls.settings && (
            <Popover
              anchorRef={gearRef}
              open={settingsOpen}
              onClose={() => setSettingsOpen(false)}
              width={230}
            >
              <IndicatorSettings
                def={controls.settings.def}
                params={controls.settings.params}
                onChange={controls.settings.onChange}
              />
            </Popover>
          )}
        </span>
      )}
    </div>
  );
}
